import React from 'react';
import {
  Box, TextField, Typography, Paper, Autocomplete, Chip, Tooltip
} from '@mui/material';

export interface LabOption {
  name: string;
  hasVendorMappings: boolean;
  source: 'config' | 'vendor' | 'both';
}

interface Props {
  labName: string;
  onLabNameChange: (name: string) => void;
  labs: LabOption[];
  disabled?: boolean;
}

/**
 * Bypass form has ONE field: Lab Name. Everything else (orderId, patient
 * demographics, test date) is set on the existing Review page exactly like
 * the normal OCR flow does — orderId entry triggers the HIMS observation
 * lookup that populates `observationData`, which Publish depends on.
 *
 * Demographics auto-populate into `extractedData` from the CSV `#`-header
 * comment block on the server side, mirroring what Gemini extracts from a PDF.
 */
const BypassForm: React.FC<Props> = ({ labName, onLabNameChange, labs, disabled }) => {
  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
        Lab
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
        Which lab produced this report. Drives parameter mapping. Auto-fills from the CSV's <code>#</code> Lab Name header if present.
      </Typography>
      <Autocomplete
        freeSolo
        disabled={disabled}
        options={labs}
        getOptionLabel={(opt) => typeof opt === 'string' ? opt : opt.name}
        value={labName}
        onChange={(_, v) => onLabNameChange(typeof v === 'string' ? v : (v?.name || ''))}
        onInputChange={(_, v) => onLabNameChange(v)}
        isOptionEqualToValue={(opt, val) => (typeof opt === 'string' ? opt : opt.name) === (typeof val === 'string' ? val : (val as any)?.name)}
        renderOption={(props, opt) => {
          const o = typeof opt === 'string' ? { name: opt, hasVendorMappings: false, source: 'config' as const } : opt;
          return (
            <li {...props} key={o.name}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <span>{o.name}</span>
                {o.hasVendorMappings ? (
                  <Tooltip title="Vendor Master has parameter mappings — auto-matching will work for this lab.">
                    <Chip label="Mapped" size="small" color="success" variant="outlined" />
                  </Tooltip>
                ) : (
                  <Tooltip title="No Vendor Master entries — every parameter will need a manual decision.">
                    <Chip label="Unmapped" size="small" color="default" variant="outlined" />
                  </Tooltip>
                )}
              </Box>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField {...params} label="Lab Name" required
            helperText={labs.length === 0 ? 'Loading…' : `${labs.length} known labs — type to filter or add a new one`}
          />
        )}
      />
    </Paper>
  );
};

export default BypassForm;
